/**
 * The scroll that asks for the next page (`KLQ-20260910-001` §F.2).
 *
 * An `IntersectionObserver` on an element at the end of a list: when it comes into view the
 * next page is requested, and the button that used to be the only way to do it stays where it
 * is as the fallback.
 *
 * **The button is not decoration and must not be removed.** A list that can only be advanced by
 * scrolling cannot be finished by somebody using a keyboard or a screen reader — the sentinel
 * never enters *their* view because they are not scrolling a viewport, they are walking a
 * document. It is also the honest answer for a browser without an observer. So: the observer is
 * the fast path, the button is the guarantee, and both call the same `fetchNextPage`.
 *
 * **One viewport of `rootMargin`.** The next page is asked for while the last row is still a
 * screen away, so the list grows before somebody reaches the bottom rather than after they have
 * been stopped by it.
 *
 * **The observer watches the scrolling ancestor, not the window.** `components/screen` puts the
 * only scrollbar on a region inside the page, so `root: null` — the viewport — would report the
 * sentinel as visible on any screen where the list happens to be short, and never report it on
 * a screen where the region scrolls under a viewport that does not. `null` remains the
 * behaviour when nothing on the way up scrolls, which is what a page-scrolling screen wants.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** The three things this hook needs from an infinite query, so it does not depend on one. */
export interface InfiniteCursor {
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

/** The nearest ancestor that scrolls, or `null` for the viewport. */
function scrollParentOf(node: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = node.parentElement;
  while (current !== null) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === "auto" || overflow === "scroll") return current;
    current = current.parentElement;
  }
  return null;
}

export function useInfiniteSentinel(
  sentinel: HTMLElement | null,
  list: InfiniteCursor,
): void {
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;

  useEffect(() => {
    if (sentinel === null || !hasNextPage || isFetchingNextPage) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const root = scrollParentOf(sentinel);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      {
        root,
        // One viewport ahead. `root === null` means the window, whose height is the measure.
        rootMargin: `${String(root === null ? window.innerHeight : root.clientHeight)}px 0px`,
      },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
    // `isFetchingNextPage` is in the list on purpose: the observer is torn down while a page is
    // in flight and rebuilt after it lands. Without that, a sentinel still on screen after the
    // new rows arrive never fires a second time — it never *re-entered*, so there is no new
    // intersection to report, and the list stops one page in.
  }, [sentinel, hasNextPage, isFetchingNextPage, fetchNextPage]);
}


/**
 * The same idea, **many sentinels at once** — one per open branch of a tree.
 *
 * A hierarchy has no single bottom edge: each open level is its own list, and a level that
 * holds more work than one fetch carries needs its own *"keep going"*. The owner asked for
 * exactly this on 2026-09-10, against an earlier claim of mine that it would mean a scroll
 * container per level. **That claim was wrong.** A sentinel is an element the reader scrolls
 * *past*; it does not need a viewport of its own, only to be in the one the tree already has.
 *
 * **One observer for all of them**, not one per branch: the number of open branches changes as
 * people click, and rebuilding an observer per change would tear down the ones that did not
 * move. Targets come and go through the returned ref callback, which is what React calls with
 * the element on mount and with `null` on unmount — so a branch that closes, or a row the
 * table's window scrolls out of the DOM, unregisters itself without anybody tracking it.
 *
 * **`onReach` must be stable** — a `useCallback`, or a setter. It is in the effect's
 * dependencies, and a function rebuilt every render would rebuild the observer every render.
 */
export function useBranchSentinels(
  onReach: (key: string) => void,
): (key: string) => (element: HTMLElement | null) => void {
  const targets = useRef(new Map<Element, string>());
  // State rather than a ref for the *count*: a ref's `.current` changing does not re-render,
  // so the effect below would never learn that the first target had arrived.
  const [generation, setGeneration] = useState(0);

  const register = useCallback(
    (key: string) => (element: HTMLElement | null) => {
      if (element === null) return;
      targets.current.set(element, key);
      setGeneration((one) => one + 1);
    },
    [],
  );

  useEffect(() => {
    if (targets.current.size === 0) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const first = [...targets.current.keys()][0] as HTMLElement;
    const root = scrollParentOf(first);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const key = targets.current.get(entry.target);
          if (key !== undefined) onReach(key);
        }
      },
      {
        root,
        rootMargin: `${String(root === null ? window.innerHeight : root.clientHeight)}px 0px`,
      },
    );
    // Elements that have left the document are dropped here rather than by a caller: the
    // table's window unmounts rows without telling anybody, and observing a detached node is a
    // leak that never fires.
    for (const element of [...targets.current.keys()]) {
      if (!element.isConnected) {
        targets.current.delete(element);
        continue;
      }
      observer.observe(element);
    }
    return () => {
      observer.disconnect();
    };
  }, [generation, onReach]);

  return register;
}
